"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type BookReportInput,
  type BookReportLevel,
  buildBookReport,
} from "@/lib/tool-kit/essay-templates/book-report";

/** English strings. A translated content file overrides these through `ui`. */
export const BOOK_REPORT_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  levelLabel: "Grade level",
  levels: {
    elementary: "Elementary school",
    middle: "Middle school",
    high: "High school",
  },
  levelHints: {
    elementary:
      "Grades 2–5: short fill-in sections — the story, the characters, a favorite part, and a recommendation.",
    middle:
      "Grades 6–8: an introduction, setting and characters, a plot summary, a theme with an example, and an opinion backed by evidence.",
    high: "Grades 9–12: an analytical report — a thesis, a brief summary, analysis with cited evidence, an evaluation, and an MLA Work Cited entry.",
  },
  bookHeading: "The book",
  studentLabel: "Your name (optional)",
  studentPlaceholder: "e.g. Maya Torres",
  titleLabel: "Book title",
  titlePlaceholder: "e.g. Bridge to Terabithia",
  authorLabel: "Author",
  authorPlaceholder: "e.g. Katherine Paterson",
  genreLabel: "Genre",
  genrePlaceholder: "e.g. Realistic fiction",
  yearLabel: "Year published",
  yearPlaceholder: "e.g. 1977",
  publisherLabel: "Publisher",
  publisherPlaceholder: "e.g. Thomas Y. Crowell",
  publisherHint: "Used for the MLA Work Cited entry in the high-school report.",
  storyHeading: "The story",
  settingLabel: "Setting — where and when",
  settingPlaceholder: "Where does the story take place, and when?",
  charactersLabel: "Main characters — one per line, “Name — description”",
  charactersPlaceholder: "e.g. Jess — a fifth grader who loves to draw",
  plotLabel: "Plot summary",
  plotPlaceholder:
    "What happens at the beginning, in the middle, and at the end?",
  themesLabel: "Themes — one per line",
  themesPlaceholder:
    "e.g. Friendship can help you face your fears\n(a message, not just one word)",
  responseHeading: "Your response",
  opinionLabel: "Your opinion",
  opinionPlaceholder: "What did you think, and why? Give examples.",
  recommendationLabel: "Recommendation",
  recommendationPlaceholder: "Who would enjoy this book, and why?",
  ratingLabel: "Rating",
  ratingNone: "No rating",
  ratingOption: "{rating} out of 5",
  fileName: "book-report",
  document: {
    heading: "Your book report",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every section has your content.",
  },
  defaultTitle: "Book Report",
  titleWithBook: "Book Report: {title}",
  byline: "By {name}",
  headings: {
    aboutBook: "About the Book",
    whereWhen: "Where and When",
    whoIs: "Who Is in the Story",
    whatHappens: "What Happens",
    bigIdea: "The Big Idea",
    favoritePart: "My Favorite Part",
    whatIThink: "What I Think",
    recommend: "Would I Recommend It?",
    introduction: "Introduction",
    settingCharacters: "Setting and Characters",
    plotSummary: "Plot Summary",
    theme: "Theme",
    opinion: "My Opinion",
    recommendationConclusion: "Recommendation and Conclusion",
    summary: "Summary",
    analysis: "Analysis",
    evaluation: "Evaluation",
    conclusion: "Conclusion",
    workCited: "Work Cited",
  },
  subheadings: {
    mainCharacters: "Main characters",
    setting: "Setting",
    characters: "Characters and conflict",
    themes: "Themes",
    craft: "Style and craft",
  },
  fields: {
    title: "Title",
    author: "Author",
    genre: "Genre",
    published: "Published",
    rating: "Rating",
  },
  ratingValue: "({rating} out of 5)",
  guidance: {
    plot: "Cover the main conflict, the most important events, and how the story ends, in one or two paragraphs. Write about the story in the present tense.",
    summary:
      "Keep the summary to about a quarter of the report — the analysis is what earns the grade. Write about the story in the present tense.",
    workCited:
      "MLA 9 format, headed Work Cited because it lists a single source. Use the publisher and year of the edition you read.",
  },
  prompts: {
    setting: "Where does the story happen, and when?",
    charactersElementary:
      "Write the name of each main character and a few words about them.",
    plotElementary:
      "Tell what happens at the beginning, in the middle, and at the end.",
    themeElementary: "What lesson does the story teach?",
    favoritePart: "Describe your favorite part and tell why you liked it.",
    opinionElementary: "Did you like the book? Give two reasons.",
    recommendElementary:
      "Finish this sentence: I would recommend this book to … because ….",
    hook: "Open with a hook — a question or a striking detail from the book — then say in one sentence what the book is about.",
    setting2:
      "Describe where and when the story takes place, and how the setting shapes what happens.",
    characters:
      "For each main character, give two traits and an example of each trait in action.",
    plot: "Summarize the main conflict, the most important events, and the resolution.",
    themes:
      "State each theme as a message, not a single word: not just “friendship”, but what the book says about friendship.",
    themeExample: "Explain one theme with a specific example from the book.",
    opinion:
      "Give your opinion and support it with two specific examples from the book.",
    recommendation: "Say who would enjoy the book, and why.",
    closing: "Close by restating your overall opinion in a fresh way.",
    context:
      "Introduce the book and its context: when it was written, and what was happening in the world it describes.",
    thesis:
      "End the introduction with a thesis: a claim about how the book develops its central theme.",
    plotHigh:
      "Summarize the plot briefly: the central conflict, the turning points, and the resolution.",
    charactersHigh:
      "Explain how the main characters change and what drives the central conflict.",
    themesHigh:
      "State the themes your analysis will focus on, each as a claim the book makes.",
    evidence:
      "Support each claim with evidence — a short quotation or a specific scene — cited with a page number in MLA style, for example ({surname} 112).",
    craft:
      "Analyze one technique the author uses — point of view, symbolism, structure, or tone — and its effect on the reader.",
    evaluation:
      "Judge the book on its own terms: what does it do well, and where is it weaker? Support each judgment with evidence.",
    conclusionHigh:
      "Return to your thesis and say what the book adds to your understanding of its theme.",
    workCited:
      "Add the author and title above to build the MLA Work Cited entry.",
  },
  authorFallback: "Author",
  exampleElementary: {
    student: "Maya Torres",
    title: "Charlotte's Web",
    author: "E. B. White",
    genre: "Animal fantasy",
    year: "1952",
    publisher: "Harper & Brothers",
    setting:
      "A farm in the countryside — first the Arables' house, then the barn on the Zuckermans' farm — and the county fair, over about one year.",
    characters:
      "Wilbur — a young pig who is friendly and worries a lot\nCharlotte — a clever, kind gray spider who lives in the barn\nFern — the girl who saves Wilbur when he is born\nTempleton — a greedy rat who helps only when there is something in it for him",
    plot: "Fern saves a runt pig named Wilbur and raises him until he is sold to her uncle, Mr. Zuckerman. In the barn, Wilbur learns that he is going to be killed for food at Christmas. His new friend Charlotte the spider makes a plan: she weaves words like “Some Pig” into her web so people think Wilbur is special. Wilbur wins a special prize at the county fair, and his life is saved. Charlotte dies after the fair, but Wilbur takes care of her egg sac, and three of her babies stay in the barn with him.",
    themes:
      "Friendship means helping each other\nEvery life matters, even a small one\nLife has seasons: some things end, and new things begin",
    opinion:
      "I liked this book because Charlotte is a true friend. The saddest part was when Charlotte died, but I was happy that her babies stayed with Wilbur. My favorite part was when the words appeared in the web and everyone came to see them.",
    recommendation:
      "I recommend this book to kids who like animals and stories about friends. Have tissues ready for the ending!",
  },
  exampleMiddle: {
    student: "Leo Park",
    title: "Hatchet",
    author: "Gary Paulsen",
    genre: "Survival fiction",
    year: "1987",
    publisher: "Bradbury Press",
    setting:
      "The Canadian wilderness in summer: a lake in the northern forest, where Brian lives in a shelter under a rock ledge for 54 days.",
    characters:
      "Brian Robeson — a 13-year-old boy from the city who has never had to survive on his own\nThe pilot — has a heart attack and dies during the flight\nBrian's mother — gave Brian the hatchet; Brian is keeping a painful secret about her",
    plot: "Brian is flying in a small plane to visit his father in Canada when the pilot has a heart attack and dies. Brian crash-lands the plane in a lake and is left alone in the wilderness with only the hatchet his mother gave him. At first he makes painful mistakes, like eating berries that make him sick. Slowly he learns to make fire with the hatchet, build a shelter, and catch fish. After a tornado wrecks his camp, he reaches the sunken plane and finds a survival pack. He switches on its emergency transmitter, and a pilot rescues him on his 54th day.",
    themes:
      "Patience and persistence matter more than strength\nMistakes are how you learn to survive\nFacing a painful truth is part of growing up",
    opinion:
      "I thought Hatchet was exciting from the first chapter because Brian's problems feel real. Paulsen describes each mistake in detail, like the hours Brian spends trying to make fire, so when he finally succeeds it feels earned. The middle of the book is slow in places, but that slowness shows how much patience survival takes.",
    recommendation:
      "I would recommend Hatchet to readers in grades 5 to 8 who like adventure stories, especially anyone who thinks they could not survive without a phone.",
  },
  exampleHigh: {
    student: "Ana Rivera",
    title: "To Kill a Mockingbird",
    author: "Harper Lee",
    genre: "Coming-of-age novel",
    year: "1960",
    publisher: "J. B. Lippincott",
    setting:
      "Maycomb, a small town in Alabama, during the Great Depression of the 1930s.",
    characters:
      "Scout (Jean Louise) Finch — the narrator, a curious and outspoken girl who is about six when the story begins\nAtticus Finch — Scout's father, a lawyer who defends Tom Robinson\nJem Finch — Scout's older brother, whose faith in justice is shaken by the trial\nTom Robinson — a Black man falsely accused of raping a white woman, Mayella Ewell\nBoo (Arthur) Radley — the reclusive neighbor the children fear, who saves them in the end",
    plot: "Scout Finch narrates three years of her childhood in Maycomb. She, her brother Jem, and their friend Dill are fascinated by their unseen neighbor, Boo Radley. When their father, Atticus, defends Tom Robinson against a false charge of rape, much of the town turns against the family. Atticus shows at trial that Tom could not have committed the crime, yet the all-white jury convicts him, and Tom is later shot while trying to escape from prison. Mayella's father, Bob Ewell, takes revenge by attacking Scout and Jem one night; Boo Radley rescues them, and Ewell is killed in the struggle.",
    themes:
      "Prejudice can defeat the truth even in a court of law\nMoral courage means doing what is right when you expect to lose\nEmpathy requires seeing the world from another person's point of view\nGrowing up means losing the belief that the world is fair",
    opinion:
      "The novel's greatest strength is its narrator: because Scout is a child, she reports Maycomb's prejudice without fully understanding it, which makes the injustice more striking for the reader. Its weakness is that Tom Robinson and Maycomb's Black community are seen only from the outside; Tom has little voice of his own, and his story serves mainly as a lesson for the white Finch children.",
    recommendation:
      "I recommend the novel to readers fourteen and up, especially alongside a history of the Jim Crow South, which explains the world the trial takes place in.",
  },
} as const;

const EMPTY: BookReportInput = {
  level: "middle",
  student: "",
  title: "",
  author: "",
  genre: "",
  year: "",
  publisher: "",
  setting: "",
  characters: "",
  plot: "",
  themes: "",
  opinion: "",
  recommendation: "",
  rating: "",
};

const LEVELS: readonly BookReportLevel[] = ["elementary", "middle", "high"];
const RATINGS = ["5", "4", "3", "2", "1"] as const;
/** The examples' ratings are settings, not prose — kept out of the translatable strings. */
const EXAMPLE_RATINGS: Record<BookReportLevel, string> = {
  elementary: "5",
  middle: "4",
  high: "5",
};

type TextKey = Exclude<keyof BookReportInput, "level" | "rating">;

export default function BookReportGeneratorWidget() {
  const strings = useToolUi(BOOK_REPORT_GENERATOR_STRINGS);
  const [input, setInput] = useState<BookReportInput>(EMPTY);
  const doc = useMemo(() => buildBookReport(input, strings), [input, strings]);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const examples = {
    elementary: strings.exampleElementary,
    middle: strings.exampleMiddle,
    high: strings.exampleHigh,
  };
  const ratingOptions = [
    { value: "", label: strings.ratingNone },
    ...RATINGS.map((value) => ({
      value,
      label: t(strings.ratingOption, { rating: value }),
    })),
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...examples[previous.level],
              level: previous.level,
              rating: EXAMPLE_RATINGS[previous.level],
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({ ...EMPTY, level: previous.level }))
          }
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <div className="space-y-2">
        <PillGroup
          label={strings.levelLabel}
          name="br-level"
          onChange={(level) => setInput((previous) => ({ ...previous, level }))}
          options={LEVELS.map((value) => ({
            value,
            label: strings.levels[value],
          }))}
          value={input.level}
        />
        <p className={HINT}>{strings.levelHints[input.level]}</p>
      </div>

      <Panel title={strings.bookHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="br-title"
            label={strings.titleLabel}
            onChange={update("title")}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
          <TextField
            id="br-author"
            label={strings.authorLabel}
            onChange={update("author")}
            placeholder={strings.authorPlaceholder}
            value={input.author}
          />
          <TextField
            id="br-genre"
            label={strings.genreLabel}
            onChange={update("genre")}
            placeholder={strings.genrePlaceholder}
            value={input.genre}
          />
          <TextField
            id="br-student"
            label={strings.studentLabel}
            onChange={update("student")}
            placeholder={strings.studentPlaceholder}
            value={input.student}
          />
          <TextField
            id="br-year"
            label={strings.yearLabel}
            onChange={update("year")}
            placeholder={strings.yearPlaceholder}
            value={input.year}
          />
          <TextField
            hint={strings.publisherHint}
            id="br-publisher"
            label={strings.publisherLabel}
            onChange={update("publisher")}
            placeholder={strings.publisherPlaceholder}
            value={input.publisher}
          />
        </div>
      </Panel>

      <Panel title={strings.storyHeading}>
        <TextAreaField
          id="br-setting"
          label={strings.settingLabel}
          onChange={update("setting")}
          placeholder={strings.settingPlaceholder}
          prose
          rows={2}
          value={input.setting}
        />
        <TextAreaField
          id="br-characters"
          label={strings.charactersLabel}
          onChange={update("characters")}
          placeholder={strings.charactersPlaceholder}
          prose
          rows={4}
          value={input.characters}
        />
        <TextAreaField
          id="br-plot"
          label={strings.plotLabel}
          onChange={update("plot")}
          placeholder={strings.plotPlaceholder}
          prose
          rows={5}
          value={input.plot}
        />
        <TextAreaField
          id="br-themes"
          label={strings.themesLabel}
          onChange={update("themes")}
          placeholder={strings.themesPlaceholder}
          prose
          rows={3}
          value={input.themes}
        />
      </Panel>

      <Panel title={strings.responseHeading}>
        <TextAreaField
          id="br-opinion"
          label={strings.opinionLabel}
          onChange={update("opinion")}
          placeholder={strings.opinionPlaceholder}
          prose
          rows={4}
          value={input.opinion}
        />
        <TextAreaField
          id="br-recommendation"
          label={strings.recommendationLabel}
          onChange={update("recommendation")}
          placeholder={strings.recommendationPlaceholder}
          prose
          rows={2}
          value={input.recommendation}
        />
        <div className="max-w-xs">
          <SelectField
            id="br-rating"
            label={strings.ratingLabel}
            onChange={(rating) =>
              setInput((previous) => ({ ...previous, rating }))
            }
            options={ratingOptions}
            value={input.rating}
          />
        </div>
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="br"
        labels={strings.document}
      />
    </div>
  );
}
